<template>
  <div class="account-alert" v-if="hasErrors && !hideAlert">
    <BaseAlert
      id="create-account-form__error"
      type="error"
      :message="errorMessage ? errorMessage : 'Errors in account form'"
      :hide-prefix="hidePrefix"
      :prefix="prefix"
    />
  </div>

  <p class="form__text">{{ formText }}</p>

  <form id="account-form" @submit.prevent="emit('onSubmit')" class="form-class" novalidate>
    <div class="row">
      <BaseInputTextValidated
        id="account-form__first-name"
        name="firstName"
        type="text"
        label="First Name"
        placeholder="Kasey"
        :disabled="disableIdentityFields"
      />
      <BaseInputTextValidated
        id="account-form__last-name"
        name="lastName"
        type="text"
        label="Last Name"
        placeholder="Koala"
        :disabled="disableIdentityFields"
      />
    </div>
    <BaseInputTextValidated
      id="account-form__email"
      name="email"
      type="email"
      label="Email Address"
      placeholder="kasey@email.com"
      :disabled="disableIdentityFields"
    />
    <BaseInputTextValidated
      v-if="!disableIdentityFields"
      id="account-form__confirm-email"
      name="emailConfirm"
      type="email"
      label="Confirm Email Address"
      placeholder="kasey@email.com"
    />
    <BaseInputTextValidated
      id="account-form__password"
      name="passwordCreate"
      type="password"
      label="Password"
      placeholder="Password"
      error-bag
    />
    <BaseInputTextValidated
      id="account-form__confirm-password"
      name="passwordConfirm"
      type="password"
      label="Confirm Password"
      placeholder="Confirm Password"
    />
    <BaseSelectValidated
      v-if="!disableSecurityQuestions"
      id="account-form__security-question"
      name="securityQuestion"
      label="Security Question"
      :options="securityQuestionOptions"
      option-label="label"
      placeholder="-- Select a Security Question --"
    />
    <BaseInputTextValidated
      v-if="!disableSecurityQuestions"
      id="account-form__security-question-answer"
      name="securityQuestionAnswer"
      type="text"
      label="Security Question Answer"
      placeholder="Security Question Answer"
    />
    <BaseCheckboxValidated id="terms" name="terms" label="Accept Terms">
      <template #label>
        <label class="terms-label" for="terms">
          I have read and agree to the
          <a href="https://www.careersafeonline.com/terms-of-use" target="_blank">Terms of Use</a>
          and have read and understand the CareerSafe
          <a href="https://www.careersafeonline.com/privacy-policy" target="_blank"
            >Privacy Policy</a
          >.
        </label>
      </template>
    </BaseCheckboxValidated>
    <BaseCheckboxValidated id="age" name="age" label="Over 13">
      <template #label>
        <label class="terms-label" for="age">
          I am at least 13 years of age and meet the requirements of the
          <a
            href="https://www.ftc.gov/legal-library/browse/rules/childrens-online-privacy-protection-rule-coppa"
            target="_blank"
            >Children's Online Privacy Protection Act of 1998</a
          >
          or I am taking this training as part of my school's approved curriculum.
        </label>
      </template>
    </BaseCheckboxValidated>

    <slot v-if="$slots.default" />
  </form>
</template>

<script setup lang="ts">
import useGetSecurityQuestionsQuery from "@/api/queries/useGetSecurityQuestionsQuery";
import { computed } from "vue";
import type { SecurityQuestion } from "@/types/index";
import BaseCheckboxValidated from "@/components/base/BaseCheckboxValidated.vue";
import BaseInputTextValidated from "@/components/base/BaseInputTextValidated.vue";
import BaseSelectValidated from "@/components/base/BaseSelectValidated.vue";
import BaseAlert from "@/components/base/BaseAlert.vue";

defineProps<{
  hasErrors: boolean;
  errorMessage?: string;
  formText?: string;
  hidePrefix?: boolean;
  prefix?: string;
  hideAlert?: boolean;
  disableIdentityFields?: boolean;
  disableSecurityQuestions?: boolean;
}>();

const emit = defineEmits(["onSubmit"]);

const { data: rawSecurityQuestions } = useGetSecurityQuestionsQuery();

const securityQuestionOptions = computed(() => {
  if (rawSecurityQuestions?.value?.data?.length > 0) {
    return rawSecurityQuestions.value.data.map(({ question_text, id }: SecurityQuestion) => ({
      value: id,
      label: question_text
    }));
  }
  return [];
});
</script>

<style scoped lang="scss">
.form-class {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.form__text {
  padding-bottom: 1rem;
}
.account-alert {
  padding-bottom: 1rem;
}
:deep(.k-field) {
  flex: 1;
}
:deep(.row) {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @include tablet {
    flex-direction: row;
  }
}
.terms-label {
  margin-left: 1rem;
}
:deep(.checkbox-container) {
  align-items: flex-start;
  display: flex;
}
</style>
